import React, { useEffect, useState, useContext } from 'react';
import { ExperienceContext } from '@thd-nucleus/experience-context';
import {
  Link,
  Typography,
  useBreakpoint,
} from '@one-thd/sui-atomic-components';
import { B2BCreditCardsFilterType } from '../../../api/b2b/payments';
import { DEFAULT_ROWS_PER_PAGE } from '../../../constants/payments-table';
import { DELETED_CARD_MSG } from '../../../constants/response-success-messages';
import { getNewPageNumber, hasNoPaymentCards } from '../../../utils/payments';
import { Spinner } from '../../shared/spinner/spinner';
import { usePayments } from '../../../hooks/use-payments';
import { CreditCard } from '../credit-card/credit-card';
import { CreditCardSortSearchBar } from '../credit-card-sort-search-bar/credit-card-sort-search-bar';
import { useIsMounted } from '../../../hooks/use-is-mounted';
import { Alert } from '../../shared/alert/alert';
import { TAlertStatus } from '../../../models/common/alert-messages';
import { PaymentsHeader } from '../payments-header/payments-header';
import { CardType } from '../../../constants/payments';
import { CreditCardTypesTextMapping } from '../../../constants/credit-card';
import { CREDIT_CARDS_PAGE_URL } from '../../../constants/links';
import { Pagination } from '../../shared/pagination/pagination';
import { PAYMENTS_PAGE_SIZE } from '../../../constants/general';
import { scrollToTop } from '../../../utils/general';
import { IPaymentCardCommonData } from '../../../models/common/payments';
import { CardSortByLabel, cardSortByMap } from '../../../constants/sort';
import { PaymentsHeaderSecondaryButtons } from '../payments-header/payments-header-secondary-buttons';
import { NoPaymentCardsMessage } from '../../shared/no-payment-cards-message/no-payment-cards-message';
import { CardViewToggle } from '../card-view-toggle/card-view-toggle';
import { TableView } from './table-view';
import { setOtherButtonSubmissionAnalytics } from '../../../utils/payments-analytics';
import { useLoyaltyMembershipDetails } from '../../../hooks/use-loyalty-membership-details';
import {
  CustomerRole,
  TCustomerRole,
  TCustomerType,
} from '../../../models/common/customer';

const showStopAllAuthorizationLink = (
  paymentCards?: IPaymentCardCommonData[],
) => {
  if (!paymentCards?.length) return null;

  return (
    paymentCards.find(
      (card) => card?.operations?.showAuthStatus === 'Authorized',
    ) || null
  );
};

interface CreditCardListProps extends JSX.IntrinsicAttributes {
  customerRole: TCustomerRole;
  customerType: TCustomerType;
}

export const CreditCardList: React.FC<CreditCardListProps> = (
  props: React.PropsWithChildren<CreditCardListProps>,
) => {
  const { customerRole, customerType } = props;
  const [deletedCreditCardIndex, setDeletedCreditCardIndex] = useState<
    number | null
  >(null);
  const {
    loading,
    creditCards,
    alertMessage,
    paginationInfo,
    currentPageNumber,
    currentSortByValue,
    getPaymentCards,
    setAlertMessage,
    submittedSearchType,
    isSearchActive,
    submittedSearchValue,
    isListView,
    setIsListView,
    proAllowanceMessage,
    getAccountProfile,
  } = usePayments();
  const isMountedRef = useIsMounted();
  const { channel } = useContext(ExperienceContext);
  const [isBulkAddAvailable, setIsBulkAddAvailable] = useState(false);

  const isB2B = customerType === 'B2B';
  const isMobile = channel === 'mobile';
  const screen = useBreakpoint('lg');
  const isSmallScreen = screen.lesser;
  const showStopAuthBtn = !!showStopAllAuthorizationLink(
    creditCards?.paymentCards,
  );
  const isAdmin = customerRole === CustomerRole.ADMIN;
  const shouldCheckForBulkAdd = !isMobile && isB2B && isAdmin;

  const paymentCards = !isB2B
    ? creditCards?.paymentCards.slice(
        PAYMENTS_PAGE_SIZE * (currentPageNumber - 1),
        PAYMENTS_PAGE_SIZE * currentPageNumber,
      )
    : creditCards?.paymentCards;

  const { hasHDWallet, hasProTradeCredit } = useLoyaltyMembershipDetails();
  const getTotalPages = () => {
    if (isB2B) {
      return Number(paginationInfo?.totalPages) || 0;
    }
    return paginationInfo?.totalCreditCards
      ? Math.ceil(paginationInfo?.totalCreditCards / PAYMENTS_PAGE_SIZE)
      : 0;
  };

  const totalPages = getTotalPages();

  useEffect(() => {
    return () => {
      setAlertMessage(null);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (shouldCheckForBulkAdd && isMountedRef.current) {
      getAccountProfile().then(({ customerProfile }) => {
        if (isMountedRef.current) {
          setIsBulkAddAvailable(customerProfile.bulkAdd || false);
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMountedRef, shouldCheckForBulkAdd]);

  const handleSuccess = (
    paymentId: string,
    message: string,
    dataTestId?: string,
  ) => {
    setAlertMessage({
      paymentId,
      alertStatus: TAlertStatus.SUCCESS,
      text: message,
      dataTestId,
    });
    if (message === DELETED_CARD_MSG.message) {
      const newPage = getNewPageNumber(
        currentPageNumber,
        totalPages,
        isB2B ? paginationInfo?.paymentsOnCurrentPage : paymentCards?.length,
      );
      // ensure user stays on the same page or on the previous page (if last payment deleted) to show delete confirmation
      onChangePage(newPage);
      return;
    }
    onChangePage(1);
  };

  const onChangePage = (newPageNumber: number) => {
    triggerGetPaymentCards(
      newPageNumber,
      submittedSearchType,
      submittedSearchValue,
      currentSortByValue.sortBy,
      !!currentSortByValue.ascendingOrder,
      currentSortByValue.pageSize,
    );
  };

  const handleError = (
    paymentId: string,
    message: string,
    dataTestId?: string,
  ) => {
    setAlertMessage({
      paymentId,
      alertStatus: TAlertStatus.ERROR,
      text: message,
      dataTestId,
    });
  };

  const triggerGetPaymentCards = (
    pageNumber: number,
    fType: B2BCreditCardsFilterType,
    fValue: string,
    sortBy: CardSortByLabel | null,
    ascendingOrder: boolean | null,
    pageSize?: number,
  ) => {
    return getPaymentCards(
      CardType.CREDIT_CARD,
      false,
      hasProTradeCredit,
      {
        page: pageNumber,
        sortBy: sortBy ? cardSortByMap[sortBy].value : undefined,
        ascendingOrder: ascendingOrder ?? undefined,
        pageSize,
      },
      {
        filterType: fType,
        filterValue: fValue,
      },
    );
  };

  const resetResults = (tableView: boolean) => {
    const pageSize = tableView ? DEFAULT_ROWS_PER_PAGE : PAYMENTS_PAGE_SIZE;
    const fType = B2BCreditCardsFilterType.NOTHING;
    const fValue = '';
    triggerGetPaymentCards(1, fType, fValue, null, null, pageSize);
  };

  const handleToggleView = () => {
    setAlertMessage(null);
    setIsListView((prevState) => {
      resetResults(!prevState);
      return !prevState;
    });
    setOtherButtonSubmissionAnalytics(
      `view toggle: ${!isListView ? 'list' : 'grid'} view`,
    );
  };

  const successMessage = alertMessage &&
    alertMessage.alertStatus === TAlertStatus.SUCCESS && (
      <div className={isMobile ? 'sui-mt-4' : 'lg:sui-mb-8'}>
        <Alert
          data-testid={alertMessage.dataTestId}
          status={alertMessage.alertStatus}
          onClose={() => setAlertMessage(null)}
        >
          {alertMessage.text}
        </Alert>
      </div>
    );

  const desktopErrorMessage = !isMobile &&
    alertMessage &&
    alertMessage.alertStatus === TAlertStatus.ERROR && (
      <div className="lg:sui-mb-8">
        <Alert
          data-testid={alertMessage.dataTestId}
          status={alertMessage.alertStatus}
          onClose={() => setAlertMessage(null)}
        >
          {alertMessage.text}
        </Alert>
      </div>
    );

  const HDCCMessage = (
    <Alert status="info" data-testid="infoMsg">
      <span className="sui-text-base sui-flex sui-flex-wrap">
        Visit the&nbsp;
        <Link href={CREDIT_CARDS_PAGE_URL}>Home Depot Credit Cards page</Link>
        &nbsp;to pay and manage your card.
      </span>
    </Alert>
  );

  const getCreditCardsList = () => {
    const creditCardList =
      paymentCards?.map((card, index) => (
        <div
          key={card.paymentId}
          className={`sui-flex ${isMobile ? 'sui-flex-col' : ''}`}
        >
          <CreditCard
            isMobile={isMobile}
            key={card.paymentId}
            card={card}
            setDeletedCreditCardIndex={setDeletedCreditCardIndex}
            index={index}
            onActionSuccess={handleSuccess}
            onActionFailure={handleError}
            canShowHDWallet={hasHDWallet}
          />
        </div>
      )) || null;
    if (
      isMobile &&
      deletedCreditCardIndex !== null &&
      creditCardList !== null
    ) {
      creditCardList.splice(deletedCreditCardIndex, 0, <>{successMessage}</>);
    }

    return creditCardList;
  };

  const mapReadableSearchValue = () => {
    let value = submittedSearchValue;
    if (
      submittedSearchType === B2BCreditCardsFilterType.cardBrand &&
      submittedSearchValue in CreditCardTypesTextMapping
    ) {
      value =
        CreditCardTypesTextMapping[
          submittedSearchValue as keyof typeof CreditCardTypesTextMapping
        ];
    }

    return value;
  };

  const readableSearchValue = mapReadableSearchValue();

  return (
    <>
      <Spinner active={loading} />
      <PaymentsHeader
        cardType={CardType.CREDIT_CARD}
        onActionSuccess={handleSuccess}
        onActionFailure={handleError}
        isBulkAddAvailable={isBulkAddAvailable}
      />
      {!proAllowanceMessage?.hideHDCC && HDCCMessage}
      {!isMobile && successMessage}
      {proAllowanceMessage && proAllowanceMessage.content}
      {desktopErrorMessage}
      <PaymentsHeaderSecondaryButtons
        cardType={CardType.CREDIT_CARD}
        showStopAuthBtn={showStopAuthBtn}
        onActionSuccess={handleSuccess}
        onActionFailure={handleError}
      />
      {!isSearchActive && hasNoPaymentCards(creditCards) && (
        <NoPaymentCardsMessage tenderType={CardType.CREDIT_CARD} />
      )}
      {(!hasNoPaymentCards(creditCards) || isSearchActive) && isB2B && (
        <>
          <div
            className={
              isListView
                ? 'sui-flex sui-items-center sui-gap-10 2xl:sui-gap-0'
                : 'sui-mb-4'
            }
          >
            <CreditCardSortSearchBar
              triggerGetPaymentCards={triggerGetPaymentCards}
              onResetResults={resetResults}
            />
            {!isSearchActive && !isSmallScreen && (
              <CardViewToggle
                isListView={isListView}
                onChangeToggle={handleToggleView}
              />
            )}
          </div>
          {isSearchActive && (
            <>
              <div className="sui-pb-5">
                <Typography variant="body-lg" weight="bold">
                  {paginationInfo?.totalSearchedCreditCards === 0
                    ? 'No'
                    : paginationInfo?.totalSearchedCreditCards}{' '}
                  Result
                  {paginationInfo?.totalSearchedCreditCards !== 1 && 's'} Found
                </Typography>
              </div>

              {!!readableSearchValue &&
                paginationInfo?.totalSearchedCreditCards === 0 && (
                  <Typography variant="body-base">
                    We couldn&apos;t find results for &quot;
                    {readableSearchValue}
                    &quot;. Review your search or try a new search.
                  </Typography>
                )}
            </>
          )}
        </>
      )}
      {isListView && !isSmallScreen && isB2B ? (
        <TableView
          triggerGetPaymentCards={triggerGetPaymentCards}
          onChangePage={onChangePage}
          onActionSuccess={handleSuccess}
          onActionFailure={handleError}
          canShowHDWallet={hasHDWallet}
        />
      ) : (
        <>
          <div
            data-testid="GridViewWrapper"
            className="sui-grid sui-grid-cols-1 sui-gap-y-4 sui-gap-x-8 md:sui-grid-cols-2 2xl:sui-gap-y-16 2xl:sui-grid-cols-3"
          >
            {getCreditCardsList()}
          </div>
          {totalPages > 1 && (
            <div className="sui-w-full sui-justify-center sui-p-5 sui-flex">
              <Pagination
                nPages={totalPages}
                onChangePage={(newPage) => {
                  onChangePage(newPage);
                  setAlertMessage(null);
                  scrollToTop();
                }}
                page={currentPageNumber}
              />
            </div>
          )}
        </>
      )}
    </>
  );
};
